import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, ChevronRight, Mic, Wifi, Cpu, CheckCircle } from 'lucide-react';
import { deviceManager } from '../../services/deviceManager'; // Import DeviceManager

const StatusWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 10;
`;

const CollapsedButton = styled(motion.button)`
  width: 40px;
  height: 120px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg} 0 0 ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  
  &:hover {
    background: ${({ theme }) => theme.colors.dark.surface};
    border-color: ${({ theme }) => theme.colors.primary[500]};
  }
  
  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.dark.textSecondary};
  }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status, theme }) => {
    switch (status) {
      case 'connected': return theme.colors.status.online;
      case 'connecting': return theme.colors.status.idle;
      case 'disconnected': return theme.colors.status.offline;
      default: return theme.colors.gray[500];
    }
  }};
  
  ${({ status }) => status === 'connecting' && `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  `}
`;

const StatusLabel = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const ExpandedContainer = styled(motion.div)`
  width: 320px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg} 0 0 ${({ theme }) => theme.borderRadius.lg};
  border-right: none;
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatusTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  
  &:hover {
    background: ${({ theme }) => theme.colors.dark.surface};
    color: ${({ theme }) => theme.colors.dark.text};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.dark.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
`;

const StatusItemLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
`;

const StatusItemValue = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ status, theme }) => {
    switch (status) {
      case 'online': return theme.colors.status.online;
      case 'offline': return theme.colors.status.offline;
      case 'connecting': return theme.colors.status.idle;
      case 'good': return theme.colors.status.online;
      case 'normal': return theme.colors.status.idle;
      case 'bad': return theme.colors.status.offline;
      default: return theme.colors.dark.text;
    }
  }};
`;

const StatusIndicatorDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ status, theme }) => {
    switch (status) {
      case 'online': return theme.colors.status.online;
      case 'offline': return theme.colors.status.offline;
      case 'connecting': return theme.colors.status.idle;
      case 'good': return theme.colors.status.online;
      case 'normal': return theme.colors.status.idle;
      case 'bad': return theme.colors.status.offline;
      default: return theme.colors.gray[500];
    }
  }};
  
  ${({ status }) => status === 'connecting' && `
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  `}
`;

const CollapsibleStatus = ({ status, ping, pingStatus, isPinned, setIsPinned }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [micStatus, setMicStatus] = useState('unknown');
  const [micActive, setMicActive] = useState(false);
  const buttonRef = useRef(null);
  const expandedRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  
  // ⚠️ POLLING OPTIMIZATION: Adaptive interval for efficient checking
  const minMicInterval = 1000; // 1 second minimum
  const maxMicInterval = 5000; // 5 seconds maximum
  let micCheckInterval = minMicInterval;
  let consecutiveStableChecks = 0;
  let micTimeoutId = null;

  useEffect(() => {
    // Initialize mic status
    checkMicrophoneStatus();
    
    // Listen for device changes from DeviceManager
    const handleDeviceChange = () => {
      console.log('🔄 [CollapsibleStatus] Device change detected, updating mic status...');
      checkMicrophoneStatus();
    };
    
    deviceManager.on('deviceChange', handleDeviceChange);
    
    // Listen for settings changes
    const handleSettingsChange = (event) => {
      if (event.detail.backgroundListening !== undefined) {
        setMicActive(event.detail.backgroundListening);
      }
    };
    
    window.addEventListener('settingsChanged', handleSettingsChange);

    return () => {
      if (micTimeoutId) {
        clearTimeout(micTimeoutId);
      }
      deviceManager.removeListener('deviceChange', handleDeviceChange);
      window.removeEventListener('settingsChanged', handleSettingsChange);
    };
  }, [micStatus]);

  const checkMicrophoneStatus = async () => {
    try {
      // Use DeviceManager to get device information
      const audioInputs = deviceManager.getAvailableInputDevices();
      
      const previousMicStatus = micStatus;
      
      if (audioInputs.length > 0) {
        setMicStatus('online');
        
        // Check if background listening is enabled
        const isBackgroundListening = localStorage.getItem('backgroundListening');
        setMicActive(isBackgroundListening === 'true');
      } else {
        setMicStatus('offline');
        setMicActive(false);
      }
      
      // ⚠️ POLLING OPTIMIZATION: Adjust frequency based on stability
      if (micStatus === previousMicStatus) {
        consecutiveStableChecks++;
        // If mic status is stable, gradually increase interval
        if (consecutiveStableChecks > 2) {
          micCheckInterval = Math.min(micCheckInterval * 1.5, maxMicInterval);
        }
      } else {
        // If mic status changed, reset to faster checking
        consecutiveStableChecks = 0;
        micCheckInterval = minMicInterval;
      }
    } catch (error) {
      console.error('Failed to check microphone status:', error);
      setMicStatus('offline');
      setMicActive(false);
      // On error, use faster interval to detect when issues are resolved
      micCheckInterval = minMicInterval;
    }
    
    // Schedule next microphone check
    micTimeoutId = setTimeout(checkMicrophoneStatus, micCheckInterval);
  };

  const handleButtonMouseEnter = () => {
    if (!isPinned) {
      clearTimeout(hoverTimeoutRef.current);
      setIsExpanded(true);
    }
  };

  const handleButtonMouseLeave = () => {
    if (!isPinned) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 300);
    }
  };

  const handleExpandedMouseEnter = () => {
    if (!isPinned) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleExpandedMouseLeave = () => {
    if (!isPinned) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false);
      }, 300);
    }
  };

  const handleClick = () => {
    setIsPinned(!isPinned);
    if (!isPinned) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  };

  const services = [
    { 
      label: 'Connection', 
      status: status === 'connected' ? 'online' : status === 'connecting' ? 'connecting' : 'offline',
      value: status === 'connected' ? 'Online' : status === 'connecting' ? 'Connecting...' : 'Offline'
    },
    { 
      label: 'Ping', 
      status: pingStatus,
      value: `${ping}ms`
    },
    { 
      label: 'AI Model', 
      status: status === 'connected' ? 'online' : 'offline',
      value: status === 'connected' ? 'Ready' : 'Offline'
    },
    { 
      label: 'System Mic', 
      status: micStatus === 'online' ? (micActive ? 'online' : 'normal') : 'offline',
      value: micStatus === 'online' ? (micActive ? 'Active' : 'Available') : 'Not Found'
    }
  ];

  return (
    <StatusWrapper>
      <AnimatePresence mode="wait">
        {isExpanded ? (
          <ExpandedContainer
            key="expanded"
            ref={expandedRef}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onMouseEnter={handleExpandedMouseEnter}
            onMouseLeave={handleExpandedMouseLeave}
          >
            <StatusHeader>
              <StatusTitle>System Status</StatusTitle>
              <CloseButton onClick={handleClick}>
                <ChevronRight />
              </CloseButton>
            </StatusHeader>

            <StatusList>
              {services.map((service) => (
                <StatusItem key={service.label}>
                  <StatusItemLabel>{service.label}</StatusItemLabel>
                  <StatusItemValue status={service.status}>
                    <StatusIndicatorDot status={service.status} />
                    {service.value}
                  </StatusItemValue>
                </StatusItem>
              ))}
            </StatusList>
          </ExpandedContainer>
        ) : (
          <CollapsedButton
            key="collapsed"
            ref={buttonRef}
            onClick={handleClick}
            onMouseEnter={handleButtonMouseEnter}
            onMouseLeave={handleButtonMouseLeave}
            initial={{ x: 0 }}
            animate={{ x: 0 }}
            exit={{ x: -10 }}
            transition={{ duration: 0.2 }}
            whileHover={{ x: 0 }}
          >
            <Activity />
            <StatusDot status={status} />
            <StatusLabel>Status</StatusLabel>
          </CollapsedButton>
        )}
      </AnimatePresence>
    </StatusWrapper>
  );
};

export default CollapsibleStatus;
